import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import '../css/AdminDashboard.css';

function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [topFailedAttempts, setTopFailedAttempts] = useState([]);
  const [referralStats, setReferralStats] = useState([]);
  const [usersBelowThreshold, setUsersBelowThreshold] = useState([]);
  const [topSuccessful24h, setTopSuccessful24h] = useState([]);
  const [topSuccessful7d, setTopSuccessful7d] = useState([]);
  const [topSuccessfulAllTime, setTopSuccessfulAllTime] = useState([]);
  const [topFailed24h, setTopFailed24h] = useState([]);
  const [topFailed7d, setTopFailed7d] = useState([]);
  const [todaysActiveUsers, setTodaysActiveUsers] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all dashboard data in a single consolidated call
        const response = await fetch('/api/auth/admin/dashboard-consolidated', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch admin dashboard data');
        }
        
        const data = await response.json();
        
        // Set all state from the consolidated response
        setDashboardData({
          recentSignups: data.recentSignups,
          highCreditUsers: data.highCreditUsers,
          hubspotConnectionsCount: data.hubspotConnectionsCount,
          portalConfigsCount: data.portalConfigsCount,
          creditUnsubscribesCount: data.creditUnsubscribesCount,
          portalConfigUnsubscribesCount: data.portalConfigUnsubscribesCount,
          allTimeSuccessfulLookups: data.allTimeSuccessfulLookups,
          trackedSuccessfulLookups: data.trackedSuccessfulLookups,
          trackedFailedAttempts: data.trackedFailedAttempts
        });
        
        setTopFailedAttempts(data.topFailedAttempts);
        setReferralStats(data.referralStats);
        setUsersBelowThreshold(data.usersBelowThreshold);
        setTopSuccessful24h(data.topSuccessful24h);
        setTopSuccessful7d(data.topSuccessful7d);
        setTopSuccessfulAllTime(data.topSuccessfulAllTime);
        setTopFailed24h(data.topFailed24h);
        setTopFailed7d(data.topFailed7d);
        setTodaysActiveUsers(data.todaysActiveUsers);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (user && user.token) {
      fetchData();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // Format date to be more readable
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Calculate success rate
  const calculateSuccessRate = () => {
    const total = dashboardData.trackedSuccessfulLookups + dashboardData.trackedFailedAttempts;
    return total > 0 ? ((dashboardData.trackedSuccessfulLookups / total) * 100).toFixed(1) : 0;
  };



  // Metric card component
  const MetricCard = ({ title, value, subtitle, icon, trend, color = '#2196F3' }) => (
    <div className="metric-card">
      <div className="metric-header">
        <div className="metric-icon" style={{ backgroundColor: color }}>
          {icon}
        </div>
        <div className="metric-info">
          <h3>{title}</h3>
          <div className="metric-value">{value}</div>
          {subtitle && <div className="metric-subtitle">{subtitle}</div>}
        </div>
      </div>
      {trend && <div className="metric-trend">{trend}</div>}
    </div>
  );

  // Mini chart component (simple bars)
  const MiniChart = ({ data, color = '#4CAF50' }) => (
    <div className="mini-chart">
      {data.map((item, index) => (
        <div 
          key={index} 
          className="mini-bar"
          style={{ 
            height: `${(item.value / Math.max(...data.map(d => d.value))) * 100}%`,
            backgroundColor: color
          }}
          title={`${item.label}: ${item.value}`}
        ></div>
      ))}
    </div>
  );

  const renderOverview = () => (
    <div className="overview-section">
      <div className="metrics-grid">
        <MetricCard
          title="Total Lookups"
          value={formatNumber(dashboardData.allTimeSuccessfulLookups)}
          subtitle="All time successful"
          icon="📊"
          color="#4CAF50"
        />
        <MetricCard
          title="Success Rate"
          value={`${calculateSuccessRate()}%`}
          subtitle="Tracked lookups"
          icon="✅"
          color="#2196F3"
        />
        <MetricCard
          title="Active Users"
          value={todaysActiveUsers.length}
          subtitle="Today"
          icon="👥"
          color="#FF9800"
        />
        <MetricCard
          title="New Signups"
          value={dashboardData.recentSignups.length}
          subtitle="Last 7 days"
          icon="🎉"
          color="#9C27B0"
        />
      </div>

      <div className="overview-charts">
        <div className="chart-section">
          <h3>System Health</h3>
          <div className="health-metrics">
            <div className="health-status-item">
              <div className="health-indicator">
                <div className="health-icon" style={{ backgroundColor: '#4CAF50' }}>🔗</div>
                <div className="health-info">
                  <span className="health-title">HubSpot Connections</span>
                  <span className="health-value">{dashboardData.hubspotConnectionsCount}</span>
                </div>
              </div>
              <div className="health-status good">Healthy</div>
            </div>
            
            <div className="health-status-item">
              <div className="health-indicator">
                <div className="health-icon" style={{ backgroundColor: '#2196F3' }}>⚙️</div>
                <div className="health-info">
                  <span className="health-title">Portal Configurations</span>
                  <span className="health-value">{dashboardData.portalConfigsCount}</span>
                </div>
              </div>
              <div className="health-status good">Active</div>
            </div>
            
            <div className="health-status-item">
              <div className="health-indicator">
                <div className="health-icon" style={{ backgroundColor: dashboardData.creditUnsubscribesCount > 10 ? '#FF5722' : '#FF9800' }}>
                  {dashboardData.creditUnsubscribesCount > 10 ? '⚠️' : '📧'}
                </div>
                <div className="health-info">
                  <span className="health-title">Credit Unsubscribes</span>
                  <span className="health-value">{dashboardData.creditUnsubscribesCount}</span>
                </div>
              </div>
              <div className={`health-status ${dashboardData.creditUnsubscribesCount > 10 ? 'warning' : 'good'}`}>
                {dashboardData.creditUnsubscribesCount > 10 ? 'High' : 'Normal'}
              </div>
            </div>
            
            <div className="health-status-item">
              <div className="health-indicator">
                <div className="health-icon" style={{ backgroundColor: calculateSuccessRate() > 90 ? '#4CAF50' : calculateSuccessRate() > 70 ? '#FF9800' : '#FF5722' }}>
                  {calculateSuccessRate() > 90 ? '✅' : calculateSuccessRate() > 70 ? '⚡' : '❌'}
                </div>
                <div className="health-info">
                  <span className="health-title">Success Rate</span>
                  <span className="health-value">{calculateSuccessRate()}%</span>
                </div>
              </div>
              <div className={`health-status ${calculateSuccessRate() > 90 ? 'good' : calculateSuccessRate() > 70 ? 'warning' : 'error'}`}>
                {calculateSuccessRate() > 90 ? 'Excellent' : calculateSuccessRate() > 70 ? 'Good' : 'Poor'}
              </div>
            </div>
            
            <div className="health-status-item">
              <div className="health-indicator">
                <div className="health-icon" style={{ backgroundColor: usersBelowThreshold.length > 5 ? '#FF5722' : '#4CAF50' }}>
                  {usersBelowThreshold.length > 5 ? '🔴' : '🟢'}
                </div>
                <div className="health-info">
                  <span className="health-title">Low Credit Alerts</span>
                  <span className="health-value">{usersBelowThreshold.length}</span>
                </div>
              </div>
              <div className={`health-status ${usersBelowThreshold.length > 5 ? 'warning' : 'good'}`}>
                {usersBelowThreshold.length > 5 ? 'Attention' : 'Normal'}
              </div>
            </div>
          </div>
        </div>

        <div className="chart-section">
          <h3>Referral Distribution</h3>
          <div className="referral-chart">
            {referralStats.map((stat, index) => (
              <div key={index} className="referral-item">
                <span className="referral-label">{stat.referral_type || 'Direct'}</span>
                <div className="referral-bar">
                  <div 
                    className="referral-fill"
                    style={{ 
                      width: `${(stat.count / Math.max(...referralStats.map(s => s.count))) * 100}%`,
                      backgroundColor: `hsl(${index * 60}, 70%, 50%)`
                    }}
                  ></div>
                  <span className="referral-count">{stat.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUserAnalytics = () => (
    <div className="analytics-section">
      <div className="analytics-grid">
        <div className="analytics-card">
          <h3>📈 Top Performers (24h)</h3>
          <div className="user-list">
            {topSuccessful24h.slice(0, 5).map((user, index) => (
              <div key={index} className="user-item">
                <div className="user-info">
                  <span className="user-email">{user.email}</span>
                  <span className="user-metric">{user.successful_lookups} lookups</span>
                </div>
                <div className="user-badge">{index + 1}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <h3>🔥 Most Active This Week</h3>
          <div className="user-list">
            {topSuccessful7d.slice(0, 5).map((user, index) => (
              <div key={index} className="user-item">
                <div className="user-info">
                  <span className="user-email">{user.email}</span>
                  <span className="user-metric">{user.successful_lookups} lookups</span>
                </div>
                <div className="user-badge">{index + 1}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <h3>💳 High Credit Users</h3>
          <div className="user-list">
            {dashboardData.highCreditUsers.slice(0, 5).map((user, index) => (
              <div key={index} className="user-item">
                <div className="user-info">
                  <span className="user-email">{user.email}</span>
                  <span className="user-metric">{user.credits} credits</span>
                </div>
                <div className="credit-indicator" style={{ backgroundColor: user.credits > 500 ? '#4CAF50' : '#FF9800' }}>
                  {user.credits > 500 ? '🟢' : '🟡'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <h3>⚠️ Low Credit Alerts</h3>
          <div className="user-list">
            {usersBelowThreshold.slice(0, 5).map((user, index) => (
              <div key={index} className="user-item">
                <div className="user-info">
                  <span className="user-email">{user.email}</span>
                  <span className="user-metric">{user.credits}/{user.credit_threshold}</span>
                </div>
                <div className="alert-indicator">🔴</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemHealth = () => (
    <div className="health-section">
      <div className="health-grid">
        <div className="health-card error-card">
          <h3>🚨 Failed Lookups (24h)</h3>
          <div className="failure-list">
            {topFailed24h.map((user, index) => (
              <div key={index} className="failure-item">
                <div className="failure-user">
                  <span className="user-email">{user.email}</span>
                  <span className="failure-count">{user.total_failed_lookups} failures</span>
                </div>
                <div className="failure-reasons">
                  {Object.entries(user.reasons).map(([reason, count]) => (
                    <span key={reason} className="reason-tag">
                      {reason}: {count}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="health-card error-card">
          <h3>📊 Failed Lookups (7d)</h3>
          <div className="failure-list">
            {topFailed7d.map((user, index) => (
              <div key={index} className="failure-item">
                <div className="failure-user">
                  <span className="user-email">{user.email}</span>
                  <span className="failure-count">{user.total_failed_lookups} failures</span>
                </div>
                <div className="failure-reasons">
                  {Object.entries(user.reasons).map(([reason, count]) => (
                    <span key={reason} className="reason-tag">
                      {reason}: {count}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="health-card">
          <h3>🎯 All-Time Champions</h3>
          <div className="champions-list">
            {topSuccessfulAllTime.map((user, index) => (
              <div key={index} className="champion-item">
                <div className="champion-rank">{index + 1}</div>
                <div className="champion-info">
                  <span className="user-email">{user.email}</span>
                  <span className="champion-count">{formatNumber(user.successful_lookups)} lookups</span>
                </div>
                <div className="champion-badge">
                  {index === 0 ? '🏆' : index === 1 ? '🥈' : index === 2 ? '🥉' : '⭐'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUserManagement = () => (
    <div className="management-section">
      <div className="management-grid">
        <div className="management-card">
          <h3>👥 Today's Active Users</h3>
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Credits Used</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {todaysActiveUsers.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.credits_used_today}</td>
                    <td><span className="status-active">Active</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="management-card">
          <h3>🆕 Recent Signups</h3>
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Credits</th>
                  <th>Signup Date</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recentSignups.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.credits}</td>
                    <td>{formatDate(user.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome back, {user.email}</p>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 User Analytics
        </button>
        <button 
          className={`tab-button ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
        >
          🏥 System Health
        </button>
        <button 
          className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
          onClick={() => setActiveTab('management')}
        >
          👥 User Management
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'analytics' && renderUserAnalytics()}
        {activeTab === 'health' && renderSystemHealth()}
        {activeTab === 'management' && renderUserManagement()}
      </div>
    </div>
  );
}

export default AdminDashboard; 